import { CreditCard, KeyRound, UserPlus } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Button, Card, CardContent, CardHeader, CardTitle } from '../components/ui';
import { useAuth, usePage } from '../contexts';
import { isOrganizationAdmin } from '../utils/organizationRole';

const OrganizationSettings: React.FC = () => {
  const { t } = useTranslation();
  const { setPageInfo } = usePage();
  const { user } = useAuth();

  React.useEffect(() => {
    setPageInfo(t('organization.title'), t('organization.subtitle'));
  }, [setPageInfo, t]);

  const canAccessOrganizationSettings = isOrganizationAdmin(user);

  if (!canAccessOrganizationSettings) {
    return (
      <Card>
        <CardContent className="p-8 text-center text-gray-600 dark:text-gray-300">
          {t('organization.ownerOnly')}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>{t('organization.manageTitle')}</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <UserPlus className="h-5 w-5 text-primary-600" />
              <h3 className="font-semibold text-gray-900 dark:text-white">
                {t('organization.invitationsTitle')}
              </h3>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
              {t('organization.invitationsDescription')}
            </p>
            <Link to="/organization-invitations">
              <Button variant="outline">{t('organization.manageInvitations')}</Button>
            </Link>
          </div>

          <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <CreditCard className="h-5 w-5 text-primary-600" />
              <h3 className="font-semibold text-gray-900 dark:text-white">
                {t('organization.activePlanTitle')}
              </h3>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
              {t('organization.activePlanDescription')}
            </p>
            <Link to="/subscription">
              <Button variant="outline">{t('organization.viewPlan')}</Button>
            </Link>
          </div>

          <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
              <KeyRound className="h-5 w-5 text-primary-600" />
              <h3 className="font-semibold text-gray-900 dark:text-white">
                {t('organization.apiKeysAndPermissionsTitle')}
              </h3>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
              {t('organization.apiKeysAndPermissionsDescription')}
            </p>
            <Link to="/organization/api-keys">
              <Button variant="outline">
                {t('organization.manageApiKeysAndPermissions')}
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default OrganizationSettings;
